import { useCallback, useState } from 'react';
import { useParams } from 'react-router-dom';

import { PalmDataGrid } from 'common/components/PalmDataGrid';
import { Roles } from 'common/constants/roles';
import { Routes } from 'common/constants/routes';
import { usePalmButtonEvaluator } from 'common/hooks/usePalmButtonEvaluator';
import { usePalmFetchEntities } from 'common/hooks/usePalmFetchEntities';
import { usePalmFilters } from 'common/hooks/usePalmFilters';
import { usePalmSortModel } from 'common/hooks/usePalmSortModel';
import { fetchLicenseUserGroups } from 'common/services/api/LicenseUserGroups/LicenseUserGroups.thunk';
import { ActionDefinition, ColumnDefinition, ColumnType, Operations } from 'common/types/TypeDefs';

import {
    addNewFilter,
    clearFilters,
    removeFilter,
    selectFilters
} from './licenseUserGroupsSlice';

export function LicenseUserGroupsTab() {
    const typeName = 'User Groups';

    const { tenantId = '', suiteId, licenseId = '' } = useParams();

    const shouldDisableOpenButton = usePalmButtonEvaluator([ Roles.Admin, Roles.MultiTenantRO, Roles.UserManager, Roles.AppManager ]);

    const [processing, setProcessing] = useState<boolean>(false);
    const [sortModel, setSortModel] = usePalmSortModel();

    const filterProp = usePalmFilters(
        selectFilters,
        addNewFilter,
        removeFilter,
        clearFilters
    );

    const onGetRowLink = useCallback((id: string) => {
        return `/${Routes.ApplicationSuites}/${suiteId}/${Routes.Tenants}/${tenantId}/${Routes.UserGroups}/${id}/${Routes.Users}`;
    }, []);

    const fetchEntities = usePalmFetchEntities(
        typeName,
        { tenantId, licenseId },
        fetchLicenseUserGroups,
        setProcessing);

    const actionDefinitions =  [
        { action: Operations.Open, disabled: shouldDisableOpenButton, urlToOpen: onGetRowLink },
    ] as ActionDefinition[];

    const columnDefinitions: ColumnDefinition[] = [
        { columnId: 'id', columnName: 'User Group ID', columnType: ColumnType.First, copy: true, shouldFilterByExactMatch: true },
        { columnId: 'name', columnName: 'User Group Name', columnType: ColumnType.Tooltip, copy: false },
        { columnId: 'description', columnName: 'User Group Description', columnType: ColumnType.Tooltip, copy: false },
        { columnId: 'actions', columnName: 'Action', columnType: ColumnType.Action }
    ];

    return <PalmDataGrid
        title={typeName}
        shouldShowCreateButton={false}
        processing={processing}
        actionDefinitions={actionDefinitions}
        columnDefinitions={columnDefinitions}
        filter={filterProp}
        operationName={Operations.Link}
        getRowLink={shouldDisableOpenButton ? undefined : onGetRowLink}
        refreshDataGridClick={() => fetchEntities()}
        sortModel={sortModel}
        clientSortHandler={setSortModel}
        shouldShowExportButton={true}
    />;
}
